'use strict';

define('vb/private/stateManagement/variable',[
  'knockout', 'signals', 'vb/private/log', 'vb/private/utils', 'jsondiff', 'vb/private/constants',
], (ko, signals, Log, Utils, JsonDiff, Constants) => {
  /**
   * @typedef {import('vb/private/stateManagement/scope.js').default} Scope
   */

  const logger = Log.getLogger('/vb/stateManagement/variable');

  const SKIP_EVAL_EXPRESSIONS_MARKER = '__vb_skip_eval_expr';
  const RETR_OLD_EVAL_MARKER = '__vb_retrieve_old_eval_value';
  const RETR_NEW_VALUE_MARKER = '__vb_retrieve_new_value';

  /**
   * Return true if the parameter is a prototype instance
   * @param {*} c
   * @returns {boolean}
   */
  const isPrototypeInstance = (c) => (!(Utils.isPrimitive(c) || Utils.isPrototypeOfObject(c) || Array.isArray(c)));

  const jsonDiff = JsonDiff.create({
    arrays: {
      detectMove: false,
    },
    cloneDiffValues: false,
  });

  /** @type {any} */
  const skipFunctionAndPrototypeDiff = (
    /** @type {any} */ context,
  ) => {
    const cl = context.left;
    const cr = context.right;
    // ignore diffing functions or instance types.
    if (typeof cl === 'function' || typeof cr === 'function') {
      context.setResult(undefined).exit();
    } else if (isPrototypeInstance(cl) || isPrototypeInstance(cr)) {
      let res;
      if (cl !== cr) {
        res = 'isDifferent';
      }
      context.setResult(res).exit();
    }
  };
  skipFunctionAndPrototypeDiff.filterName = 'skipFunctionAndPrototypeDiff';

  // this skips diffing functions, and instance types as diffing fails or goes into weird recursions
  jsonDiff.processor.pipes.diff.before('trivial', skipFunctionAndPrototypeDiff);

  /**
   * A variable is a basic unit of state. It can be a primitive, structure, collection, or Jet
   * data type.
   *
   * At runtime it does not have any intrinsic structure, although once set, it will freeze
   * the structure so that it cannot be modified unless set otherwise.
   *
   * FIXME add more doc when this is fleshed out...
   */
  class Variable {
    /**
     * Creates a new variable in the given scope and name. An initial value is optional, but
     * when provided will initialize the state in the store. The default value is what is specified
     * in the variable definition. It is the same as the initial value except for the case where
     * the variable is persisted, in which case, the initial value would be the persisted value.
     *
     * If the variable has a structure in the initial value, the object will be frozen on get,
     * such that adding new properties will not be possible unless a new structure is set on the
     * variable.
     *
     * @param {Scope}  scope          The scope on which this variable is active on // FIXME
     * @param {string} name           The name of the variable in the scope
     * @param {string} namespace      The namespace of the variable in the scope
     * @param {*} type                The type as described in getType()
     *                                used when creating new instances of this variable
     * @param {*}      defaultValue   An optional value used for the variable default value
     * @param {*}      initialValue   An optional value used for the variable initial value
     * @param {Object} descriptor     An object whose properties defines the behavior of the
     *                                variable, for example,
     *                                { writable: true }, whether variable can be written into,
     *                                { rateLimit: 100 } the rate limit in milliseconds for
     *                                limiting how often onValueChanged should be fired.
     *                                { input: String } the input value for the variable
     *                                descriptor ('fromUrl', 'fromCaller', ...)
     *                                { writableOptions: object }, where one of the options
     *                                is 'propertiesWritable', which can be 'all' or 'none'.
     *                                'all' means all properties can be set, 'none' implies none
     *                                can be set.
     *                                { dependencies: Object } a Map of dependencies (on other variables) for the
     *                                current variable. Contains 2 properties
     *                                - expressions - Array of (variable) expressions that this variable
     *                                configuration references
     *                                - variables - Array of Objects representing the referenced variable info;
     *                                where each Object contains the following properties - scopeName, variableName
     */
    constructor(scope, name, namespace, type, defaultValue, initialValue, descriptor) {
      this.log = logger;
      this.scope = scope;
      this.name = name;
      /**
       * @type {string}
       */
      this.namespace = namespace;
      this.descriptor = descriptor;

      this.type = type;
      /**
       * @type {*}
       * @protected
       */
      this._initialValue = initialValue;
      this.defaultValue = defaultValue;

      this.dirtyDataBehavior = (descriptor && descriptor.dirtyDataBehavior !== undefined)
        ? descriptor.dirtyDataBehavior
        : Constants.DirtyDataBehavior.NONE;

      /**
       * @type {signals.Signal|null}
       */
      // some scopes do not require valueChanged event to be triggered on its variables, like action chain
      this.onValueChanged = (this.scope.silent === false) ? new signals.Signal() : null;

      /**
       * @type {signals.Signal|null}
       * @private
       */
      this.onValueChangedImmediate = null; // signal created when there are immediate dispatch listeners

      this.triggerObservable = ko.observable();
      this.extendedType = false;

      // state objects used to consolidate multiple updates, that arrive before the VCL event is dispatched
      this.eventThrottleState = null; // timeoutId used setTimeout event dispatch
      this.eventMicroTaskState = null;

      this.writableOptions = (descriptor && descriptor.writableOptions) || {};

      if (!this.namespace) {
        this.namespace = Constants.VariableNamespace.VARIABLES;
        this.log.warn('variable created without a namespace, using', this.namespace, ':', name);
      }
      // variable is considered to be in 'init' stage at this point. Though primitive and object variable types may
      // not have an explicit init() method, extended types do.
      this.lifecycleStage = Constants.VariableLifecycleStage.INIT;
      this.log.finer('initializing variable', this.name);
      this.typeClassification = Constants.VariableClassification.REGULAR;

      this.computedValue = null; // initialized in getValue()
      this.oldComputedValue = null; // initialized in getValue()

      /**
       * Indicates whether a variable has been updated (regardless of whether its value changed or not)
       * @type {boolean}
       */
      this._isUpdated = false;

      /**
       * Used in instanceFactoryVariable.ja
       * @protected
       */
      this.createMethod = null;
    }

    /**
     * Name of the property to address the value of the variable in the store
     * Format for a variables named foo is 'variables@foo'.
     * @type {String}
     */
    get stateProperty() {
      return `${this.namespace}@${this.name}`;
    }

    /**
     * Return true if the parameter is a prototype instance
     * @param {*} c
     * @returns {boolean}
     */
    static isPrototypeInstance(c) {
      return isPrototypeInstance(c);
    }

    getInitialState() {
      if (this._initialValue === undefined) {
        return undefined;
      }
      return this.getStoreInitialValue();
    }

    /**
     * returns the initial value to store
     * @return {*}
     */
    getStoreInitialValue() {
      return this._initialValue;
    }

    /**
     * Adds a listener to the valueChanged event signal, which is managed privately
     * @param {(...params: any[]) => void} listener callback
     * @param {object} context in which listener is called
     * @param {boolean} [dispatchImmediate = false] - when true, value changes are delivered immediately
     * to the listener, via a microtask.
     * An example of this is the rules engine listeners that are notified immediately
     */
    addValueChangedListener(listener, context, dispatchImmediate = false) {
      if (dispatchImmediate) {
        this.onValueChangedImmediate = this.onValueChangedImmediate || new signals.Signal();
        this.onValueChangedImmediate.add(listener, context);
      } else {
        this.onValueChanged.add(listener, context);
      }
    }

    /**
     * removes the listener from the onValueChanged signal, detaching any bindings as well.
     * @param listener
     * @param context especially needed if same listener is called with multiple context.
     */
    removeValueChangedListener(listener, context) {
      this.onValueChanged.remove(listener, context);
    }

    /**
     * removes the listener from the onValueChangedImmediate signal, detaching any bindings as well.
     * @param listener
     * @param context especially needed if same listener is called with multiple context.
     */
    removeValueChangedListenerImmediate(listener, context) {
      if (this.onValueChangedImmediate) {
        this.onValueChangedImmediate.remove(listener, context);
      }
    }

    /**
     * Returns whether the tracking of dirty data is enabled for the variable.
     * @return {boolean} true if the dirty data tracking is enabled; false otherwise
     */
    isDirtyDataTrackingEnabled() {
      // we currently don't support dirty data tracking for any kind of SDPs
      const type = this.getType();
      return Utils.isInstanceType(type)
        && (type.indexOf('vb/ServiceDataProvider') >= 0 || type.indexOf('vb/MultiServiceDataProvider') >= 0)
        ? false
        : this.namespace === Constants.VariableNamespace.VARIABLES
          && this.dirtyDataBehavior === Constants.DirtyDataBehavior.TRACK;
    }

    /**
     * Returns true if value comes from rules. Not all interface constants will have its value come from rules. For
     * instance an interface constant which uses defaultValue in base and factory will simply continue to work as
     * before.
     * @returns {boolean}
     */
    isValueFromRules() {
      return !!(this.descriptor && this.descriptor.useRules === true);
    }

    /**
     * Returns whether the variable has been updated.
     * @return {boolean} true if the variable has been updated; false otherwise
     */
    isUpdated() {
      if (this.extendedType) {
        const instanceVar = this.scope.getVariable(`${this.name}${Constants.BuiltinVariableName.VALUE}`);
        // eslint-disable-next-line no-underscore-dangle
        return instanceVar._isUpdated;
      }

      return this._isUpdated;
    }

    /**
     * Re-sets the state indicating that this variable has been updated.
     */
    resetUpdatedState() {
      if (this.extendedType) {
        const instanceVar = this.scope.getVariable(`${this.name}${Constants.BuiltinVariableName.VALUE}`);
        // eslint-disable-next-line no-underscore-dangle
        instanceVar._isUpdated = false;
      }

      this._isUpdated = false;
    }

    /**
     * Retrieves a value of the variable being tracked for dirty data changes.
     * If a variable is of extended type, the referenced variable value will be retrieved
     * for tracking purposes only.
     * @returns {*} The value of the variable
     */
    getValueForDirtyData() {
      if (this.extendedType) {
        // only an instance of extendedType has an internal variable for its properties
        const instanceVar = this.scope.getVariable(`${this.name}${Constants.BuiltinVariableName.VALUE}`);
        if (instanceVar) {
          return Utils.cloneObject(instanceVar.getValue());
        }
      }

      return Utils.cloneObject(this.getValue());
    }

    /**
     * Determines whether the variable has been dirtied.
     * @param {*} originalValue the original value of the variable
     * @return {boolean} true if the variable is dirty; false otherwise
     */
    isDirty(originalValue) {
      return this.isUpdated() ? !!Variable.diff(originalValue, this.getValueForDirtyData()) : false;
    }

    /**
     * Re-sets the dirty data state of the variable.
     */
    resetDirtyData() {
      if (this.isDirtyDataTrackingEnabled() && this.isUpdated()) {
        this.scope.resetVariableDirtyData(this);
      }
    }

    /**
     * Returns the value of the variable, or null if the variable is set but does not
     * currently have a value.
     *
     * This is an observable value - meaning that if the value changes the events will be
     * propagated automatically when referenced within an expression.
     *
     * @param {boolean} [raw=false] Whether or not to return the object 'as is' from the store
     * @returns {*} The value of the variable
     */
    getValue(raw = false) {
      if (!this.computedValue) {
        this.computedValue = this.getComputedValueObservable(raw);

        // if we are bound to an expression, and that expression changes, the store value
        // for this variable will not change (it will remain the expression).
        // We therefore rely on ko to detect these other changes so that we can continue to fire
        // the right event. We will cancel this event if there is a real 'set' event so only one
        // event is thrown.
        this.computedValue.subscribe((oldValue) => {
          this.oldComputedValue = oldValue;
        }, null, 'beforeChange');
        this.computedValue.subscribe((newValue) => {
          this._isUpdated = true;
          // since we lose the old value, we capture the last evaluated expressions to use for
          // the old value for these types of events
          const oldValue = this.oldComputedValue;
          this.oldComputedValue = null;
          const lastEvaluatedValue = Variable.cloneWithMarkers(oldValue, [RETR_OLD_EVAL_MARKER]);
          this.handleValueObservableChange(lastEvaluatedValue, newValue);
        });
      }

      return this.computedValue();
    }

    /**
     * the only time this gets used is from interface constants that are evaluating their value using rules. The
     * expression used may look like this - "{{ $page.getVariable('foo', 'constants).rulesValue }}" and this is
     * purely an internally used expression usage, allowing for updates to propagate to subscribers. getVariable()
     * is already a part of most container scopes. It is merely the rulesValue property in the store that gets updated.
     * @see container.createConstant
     * @returns {*}
     */
    get rulesValue() {
      // rules values are returned as is
      return this.getValueFromStore();
    }

    /**
     * @param {boolean} [raw=false]
     * @returns {*} value
     */
    getComputedValueObservable(raw = false) {
      return ko.computed(() => {
        this.triggerObservable();

        return this.getValueFromStore(raw);
      });
    }

    /**
     * Gets the value directly from the store. This will always recompute expressions.
     *
     * @param {boolean} [raw=false] Whether or not to return the object 'as is' from the store
     * @returns {*}
     */
    getValueFromStore(raw = false) {
      let val = this.scope.getState(this);

      if (val === undefined) {
        return undefined;
      }

      if (!raw) {
        val = this.getWrappedValue(val);
      }

      // seal the object so that new properties cannot be added
      // return val !== null ? Object.seal(val) : null;
      // FIXME action chains require these to be updated
      return val;
    }

    /**
     * Given a value returns the wrapped value
     * @param value
     * @return {*}
     */
    getWrappedValue(value) {
      let val = value;
      val = Utils.resolveIfObservable(val);

      // clone the object so no one mutates the store directly, then wrap so we are
      // notified when the properties are changed so we can update the base object
      if (this.extendedType || Utils.isExtendedType(val)) {
        this.extendedType = true;
        // though we don't clone the extended type variable by calling cloneObject(), we
        // also don't want to wrap it, as only its value and internalState variables are
        // mutable.
        return val;
      }
      val = Utils.cloneObject(val);
      this.wrapObject(val);
      return val;
    }

    /**
     * returns the serialized state for this variable. Discard any properties that are class instances because there
     * might be circular dependencies
     * @param value
     * @return {String}
     */
    // eslint-disable-next-line class-methods-use-this
    serialize(value) {
      // TODO:  are there issues with dropping instances entirely?
      const REPLACER_FUNC = (k, v) => ((Utils.isCloneable(v) || Utils.isPrimitive(v)) ? v : undefined);
      return JSON.stringify(value, REPLACER_FUNC);
    }

    /**
     * Sets a new value for the variable. If the value has a structure, the structure will be
     * frozen until the next time a value is set.
     *
     * To unset the variable, assign the new value as 'null'.
     *
     * @param {*} value The new value.
     * @param {Object} options
     * @param {string} options.valueFrom - property set to 'rules' when the update comes from rulesEngine. This must
     * be set when the value of a variable that is rules based is mutated. All other writes are ignored.
     */
    setValueInternal(value, options = { valueFrom: 'inline' }) {
      // if the scope has not yet been hooked up with the store, simply change the initialValue
      // this can occur in a personalization variable if retrieval of the data happens quickly

      if (this.isValueFromRules() && options.valueFrom !== 'rules') {
        const errMsg = `The value of the constant ${this.name} can be mutated only by the rules engine.`
          + 'Please check your configuration.';
        throw new Error(errMsg);
      }

      if (!this.scope.isStoreInitialized) {
        this._initialValue = value;
        return;
      }

      const currentValue = this.getValueFromStore(); // get it directly from the store

      // clone the object to get rid of getters and setters and restore the original values
      // (which will have expressions) - this will only clone the object if it was previously
      // returned from a getValue(). otherwise this will just return the object
      // the idea is to remove the evaluated expression values from the object and restore
      // the raw values, which can include the raw expression functions.
      // since brand new values are not wrapped (and have their expressions evaluated, we
      // do nothing for those cases
      // furthermore, this is only called when the entire object is set, which would be
      // very rare. if a user sets a property of that object, we will optimize so that we
      // do not have to do a deep clone
      const unwrappedValue = Variable.unwrapAndClone(value);

      // Only log when level is finer. This is to avoid fetching the value unnecessarily.
      if (this.log.isFiner) {
        if (ko.isObservable(value)) {
          this.log.finer('Updating variable', this.name, 'to an expression =', value());
        } else {
          this.log.finer('Updating variable', this.name, 'to', unwrappedValue);
        }
      }

      // update the store and event
      this.updateState(currentValue, unwrappedValue);
    }

    /**
     * Sets a new value for variable if the variable is not read-only.
     * @param {*} value The new value.
     */
    setValue(value) {
      if (this.isWritable()) {
        this.setValueInternal(value);
      }
    }

    updateState(oldValue, value) {
      this.scope.updateState(this, value);
      if (this.scope.silent === false) {
        // we need to re-fetch the variable otherwise we will not evaluate expressions
        // contained within it (then freeze it to make sure no one attempts to write to it)
        const newValue = this.getValue();
        this.dispatchChangeEvent(oldValue, newValue);
      }
    }

    /**
     * Fires an event when the observable has detected a change from ko. If this change was
     * caused by a 'set' operation, the eventing from that operation will cancel out this event
     * (so only one event is sent). Thus, any events from this method that are actually sent
     * *should* be for the following scenario:
     *
     * - We have an expression bound to some other variable
     * - That variable's value has changed
     *
     * @private
     * @param oldValue The old value of this variable
     * @param value The new value of this variable
     */
    handleValueObservableChange(oldValue, value) {
      if (!this.onValueChanged) {
        return;
      }
      // Here we record events that are caused with no explicit set (indirect write) - for
      // example our value contains an expression that uses some other variable - and that
      // variable value change.
      // if there is already an event throttle timer in queue this event value is set on the
      // same event throttle timer.

      this._throttleValueChangeEvent(oldValue, value);
    }

    /**
     * Fires an event that the value has changed. This will throttle events so that multiple
     * simultaneous events will be attempted to be delivered together. This is called only when
     * the value of the variable is directly set.
     *
     * @protected
     * @param oldValue The old value of this variable
     * @param value The new value of this variable
     */
    dispatchChangeEvent(oldValue, value) {
      if (!this.onValueChanged) {
        return;
      }

      // Here we record events that are caused with an explicit set (direct write). When a previous
      // ko change started a timer that is pending and we have a set coming in, then the new
      // changes are piggy backed on the same eventThrottle state. Otherwise a new timer is created.

      this._throttleValueChangeEvent(oldValue, value);
    }

    /**
     * Throttles dispatching the onValueChanged event by either starting a timer or enqueues a microTask, using an
     * identifier to capture extra state including the actual timer, if a timer with that identifier doesn't
     * already exist. The term 'timer' is used loosely to refer to the delayed task.
     *
     * 1) Timer Behavior (using setTimeout task):
     * The timeout used by default is 1ms unless the variable is explicitly
     * configured with a rateLimit.timeout value.
     * If a timer with that identifier was already started the new value is updated on the timer
     * state.
     *
     * Note: If page author were to set timeout to 0 or a negative value the actual delay
     * browser uses varies and is at least 4 ms.
     * See MDN reference - the specification requires that there is a minimum timeout.
     * (https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/setTimeout#Specification)
     * If you provide something less than this (HTML5 spec says 4ms) then the browser will just
     * ignore your delay and use the minimum. The schema description has been updated to reflect
     * this.
     *
     * Often page authors end up updating multiple properties on complex variables like an SDP
     * variable and expect the changes to be reflected on it and UI right away (iow,
     * synchronously). But it's ok for valueChanged event firing to be throttled. VB recommends
     * user specify a throttle using rateLimit.timeout on the variable. Throttling must work
     * consistently in all of these cases (see below). For the above SDP example there are at
     * least 4 ways the variable properties can be changed. These rules are applicable to all
     * complex variable updates.
     *
     * Example: an SDP variable that is defined like below
     * sdp: {
       *   defaultValue: {
       *     sort: "{{ $variables.foo.sort }}"
       *     filter: "{{ $variables.foo.filter }}"
       *   },
       *   rateLimit: {
       *     timeout: 50
       *   }
       * }
     *
     * Case 1: Direct Updates
     * here multiple SDP properties are updated directly via assignVariables action or
     * some UI writing to the property directly
     *
     * Case 2: Indirect Updates
     * here multiple SDP properties are updated indirectly, by writing to the referenced
     * variables via assignVariables action, or some UI writing to the variable directly
     *
     * Case 3: Mixed Update: Direct update followed by Indirect
     * here one property is updated directly using assignVars action or a UI write -
     * $variables.sdp.sortCriteria, and the second SDP property updated indirectly -
     * $variables.foo.filter.
     *
     * Case 4: Mixed Update: Indirect Update followed by Direct
     * - Same as Case 3 but the order is flipped.
     *
     * 2) Timer Behavior when Microtasks are involved
     *  When require-ut is enabled action module loading is faster causing a slight delay in dispatching
     *  onValueChanged signal. This essentially means that a variable update (particularly via assignVars or
     *  restVars), will finish and the subsequent action will start before the VCLs' logic is called, leading to
     *  inconsistencies in the expected and actual values of the variable in subsequent actions. Suffice to say the
     *  order of ops differ between require and require-ut use, potentially causing regressions in app configured
     *  behavior, when they move to require-ut. Some regressions and possible fixes are discussed below:
     *  - fragment writeback VCL: may want to be notified quicker, when require-ut is enabled. see VBS-43124 for details
     *  - rules engine VCL: some rules engine tests fail with require-ut, as assignment to a variable, triggers a
     *  delayed dispatch to the rules VCL, causing a subsequent action (callModuleFunction) to fail, as it expects
     *  rules constants to have been updated. For rules, it might be better to use always use microtask. So each
     *  stakeholder that registers a VCL can tell a variable how it wants the change dispatched - immediately or on
     *  a timeout (default)
     *
     *  Note: the JS event loop runs all queued microtasks right after a task completes, including microtasks that
     *  are enqueued by the processing microtask (MT). Example,
     *   1. assignVarsAction updates a variable, causing VCLs for 'immediate dispatch' to be enqueued. Additionally
     *   timer-based VCLs are set to execute after a 'minimum' timeout.
     *   2. Once the current task exits, the event loop checks MT queue and starts running them. So rules VCL will
     *   run, potentially updating a constant's (c1) value, which in turn queues another microtask. This causes the
     *   'c1' rules VCL to run as well.
     *
     * @param oldValue
     * @param value
     * @param {String} throttleTimerId identifier of the timer
     * @private
     */
    _throttleValueChangeEvent(oldValue, value, throttleTimerId = 'eventThrottleState') {
      const hasImmediateVCLs = !!(this.onValueChangedImmediate
          && this.onValueChangedImmediate.getNumListeners() > 0);
      const hasDelayedVCLs = this.onValueChanged.getNumListeners() > 0;
      const hasValueChangedListeners = hasDelayedVCLs || hasImmediateVCLs;
      if (hasValueChangedListeners) {
        /**
         * Callback that throttles dispatch of VCLs, either by queuing a microtask or a setTimeout. Historically this
         * was dispatched on a setTimeOut(1ms (default) or configured via rateLimit.timeout). But with require-ut
         * enabled or in some cases always, we queue a microtask to force an immediate notification to listeners
         * that request the same.
         * @param {string} ttId
         */
        const dispatchValueChangedEvent = (ttId) => {
          let tObj;
          try {
            // the setTimeout timerId can and must be cleared as the closure hangs onto the variable / state after
            // it has been disposed, and calls the handler. But queued microTasks cannot be canceled so any queued
            // task handler will be called. So bail if 'this' access fails
            tObj = this[ttId];
            // eslint-disable-next-line no-unused-vars
          } catch (e) {
            return;
          }

          const { dispatchImmediate } = tObj;
          const ov = tObj.oldValue;
          const nv = tObj.value;
          this[ttId] = null;

          // calculate the diffs once;
          let diff;
          if (typeof ov !== 'function' && typeof nv !== 'function') {
            diff = Variable.diff(ov, nv);
          }
          const ep = {
            name: this.name,
            namespace: this.namespace,
            type: Constants.VALUE_CHANGED,
            oldValue: ov,
            value: nv,
            diff,
          };

          // dispatch the event if there's a diff
          if (diff) {
            if (dispatchImmediate === true) {
              // only call the tracked immediate binding handlers
              this.onValueChangedImmediate.dispatch(ep);
            } else {
              this.onValueChanged.dispatch(ep);
            }
          }
        };

        /**
         * Set up a dispatcher that dispatches a valueChanged event using a microTask or a timer.
         * @param {string} [stateObjKey = throttleTimerId], can be either 'eventMicroTaskState' or throttleTimerId param
         */
        const setupDispatcher = (stateObjKey = throttleTimerId) => {
          if (stateObjKey && !this[stateObjKey]) {
            this[stateObjKey] = {
              oldValue,
              value,
            };

            if (stateObjKey === 'eventMicroTaskState') {
              this[stateObjKey].dispatchImmediate = true;
              this[stateObjKey].timer = queueMicrotask(() => dispatchValueChangedEvent(stateObjKey));
            } else {
              // use the rateLimit specified in the descriptor or the default rate limit
              const rateLimit = this.descriptor && this.descriptor.rateLimit ? this.descriptor.rateLimit : {};
              const timeout = rateLimit.timeout !== undefined
                ? rateLimit.timeout : Constants.DEFAULT_RATE_LIMIT;

              this[stateObjKey].timer = setTimeout(() => dispatchValueChangedEvent(throttleTimerId), timeout);
            }
          } else if (Variable.diff(this[stateObjKey].value, value)) {
            // note: we do not update the old value since we want the old value to come from the
            // original event, since we are throttling the events
            // sometimes we get multiple updates for the same value; guard against that

            // for microtasks:
            // As an optimization we consolidate multiple updates before the queued MT runs. So rather than queue
            // two MTs, we update the state of the current MT. Example, a variable fullName depends on two variables
            // - firstName and lastName - "fullName": "{{ $variables.firstName + $variables.lastName }}"
            //
            // An assignVarsAction updating both firstName and lastName can end up causing two MTs to be enqueued
            // for fullName.
            this[stateObjKey].value = value;
          }
        };

        if (hasImmediateVCLs) {
          setupDispatcher('eventMicroTaskState');
        }

        if (hasDelayedVCLs) {
          setupDispatcher();
        }
      }
    }

    /**
     * Creates a new value for the Variable. If 'withDefaults' is true, this will include the default
     * value(s) for this variable, otherwise, just an empty structure will be returned.
     *
     * @returns {*}
     */
    createNewValue() {
      return Utils.cloneObject(this.defaultValue);
    }

    /**
     * Returns the type for this variable.
     *
     * A type can be a primitive, i.e. "string", "boolean", or "number".
     *
     * It can also be an object. In this case, the type will be returned as an object, where the
     * keys are the names of the properties, and the values are the types for those keys. The
     * types for keys can also be objects or any other type.
     *
     * A a type can also be an array, in which case it will be represented as a single item array
     * where that item describes the type of each item in the array. If the array is an array of
     * primitives, that will be returned as a string such as "string[]", "boolean[]", or "number[]".
     *
     * A type can be an extended type (meaning it is an instance of some class). In this case, the
     * type is the string representation of the module (i.e. "vb/ServiceDataProvider").
     *
     * Finally, a type can be a wildcard object. In this case the type is a string, "any". If it's
     * an array whose items can be wildcards, this is represented as "any[]".
     *
     * @return {*} The variable type
     */
    getType() {
      return this.type;
    }

    /**
     * Notifies the variable that it's underlying state has changed. Should only be called
     * by the scope to notify about a change in the store.
     */
    trigger() {
      this.triggerObservable.notifySubscribers();
    }

    /**
     * Returns the change set between the old and new values.
     *
     * See: https://github.com/benjamine/jsondiffpatch/blob/master/docs/deltas.md
     *
     * @private
     * @param {*} oldValue The old value
     * @param {*} newValue The new value
     * @returns {Object} A delta format for what has changed
     */
    static diff(oldValue, newValue) {
      // If a value is not cloneable, it means that it's an instance of a class and may have functions.
      // JSON diff will throw an exception when comparing values containing functions. Therefore, perform
      // identity comparison instead.
      if (!Utils.isCloneable(oldValue) || !Utils.isCloneable(newValue)) {
        return oldValue !== newValue;
      }
      return jsonDiff.diff(oldValue, newValue);
    }

    /**
     * Sets up getter and setter wrappers for all properties of the variable's value object.
     * Watches all properties of struct like value, such that modifying any one of the properties
     * will result in the the whole variable being resent to the store. If set is disabled the
     * setter logs an error.
     *
     * @private
     * @param {Object} obj The root or parent object that represents the value of this variable
     */
    wrapObject(obj) {
      this.traverseObject(obj, obj, (po, propKey, pv) => {
        const root = obj;
        const propValue = pv;
        const parentObject = po;

        const propDescriptor = Object.getOwnPropertyDescriptor(po, propKey);
        if (propDescriptor.configurable) {
          const deletedProp = delete parentObject[propKey];

          if (deletedProp) {
            const val = propValue;
            let newValue;
            let isDirty = false;

            // we sometimes rely on knockout to deliver change events - when this happens, we lose
            // the 'old' value since the old value will be evaluated in the context of the new
            // value. we therefore keep the last evaluated value in order to provide the correct
            // change events (we can consider moving this to the store directly for
            // improved debugging).
            let lastEvaluatedValue = val;

            // when the propVal is an expression, evaluate the ko computed. Only do this for ko observables.
            if (ko.isObservable(lastEvaluatedValue)) {
              // Cache the initial evaluation so we can establish the proper old value when
              // notified of beforeChange by knockout. We also need to clone the value to
              // prevent the side effect of updates to the original value changing the
              // lastEvaluatedValue.
              lastEvaluatedValue = Utils.cloneObject(lastEvaluatedValue());
            }

            const propertyDescriptorDef = {
              get: () => {
                if (Object.prototype.hasOwnProperty.call(root, RETR_NEW_VALUE_MARKER) === true
                  && isDirty) {
                  return newValue;
                }

                if (Object.prototype.hasOwnProperty.call(root, RETR_OLD_EVAL_MARKER) === true) {
                  return lastEvaluatedValue;
                }

                if (typeof val === 'function'
                  && typeof val.subscribe === 'function'
                  && Object.prototype.hasOwnProperty.call(root, SKIP_EVAL_EXPRESSIONS_MARKER) === false) {
                  lastEvaluatedValue = Utils.cloneObject(val());
                  return lastEvaluatedValue;
                }

                return val;
              },
              enumerable: true,
              configurable: true,
            };

            if (this.writableOptions.propertiesWritable === Constants.VariableWritablePropertyOptions.NONE) {
              propertyDescriptorDef.set = (nv) => {
                // explicit writes to properties are disallowed when disabled
                this.log.error('Cannot set the value', nv, 'to property ', propKey, 'of the'
                  + ' variable \'', this.name, '\'as it disallows property writes. Use VB'
                  + ' actions like assignVariablesAction to update the variable instead.');
              };
            } else {
              propertyDescriptorDef.set = (nv) => {
                isDirty = true;
                // when a new value is set, it is important that we not mutate the original
                // object in redux (i.e., val), instead we track the new value (via closure).
                // This is done to not break the immutability requirement of redux state. E.g.,
                // one place where this manifests is a JET component holding on to the old
                // property (object) because it is needing to compare with a new value.
                newValue = nv;

                // TODO - we should do an optimized set here to avoid the deep clone
                this.setValue(obj);
              };
            }
            Object.defineProperty(parentObject, propKey, propertyDescriptorDef);
          }
        }
      });
    }

    // eslint-disable-next-line default-param-last
    traverseObject(root, parentObject = root, propertyHandler) {
      if (Array.isArray(parentObject)
        || (Utils.isObject(parentObject) && Utils.isPrototypeOfObject(parentObject))) {
        Object.keys(parentObject).forEach((propKey) => {
          this.setupPropertyHandler(parentObject, propKey, propertyHandler, root);
        });
      }
    }

    setupPropertyHandler(parentObject, propKey, propertyHandler, root) {
      const propValue = parentObject[propKey];
      const propType = typeof propValue;

      if (propType === 'boolean' || propType === 'number' || propType === 'string'
        || propType === 'function' || propValue === null || propValue === undefined) {
        propertyHandler.call(this, parentObject, propKey, propValue);
      } else if (propType === 'object' && Array.isArray(propValue)) {
        // handle the array itself
        propertyHandler.call(this, parentObject, propKey, propValue);

        // handle all the items in the array
        this.traverseObject(root, propValue, propertyHandler);
      } else if (propType === 'object') {
        this.traverseObject(root, propValue, propertyHandler);
      }
    }

    isWritable() {
      return !this.descriptor || this.descriptor.writable !== false;
    }

    /**
     * Called during the 'activate' lifecycle stage. This stage is particularly important for extended types so they
     * can use this opportunity to read property values and other activation related tasks.
     * @param {Scope} currentScope
     * @param {Object} availableContexts
     *
     * @return {Promise} resolves when variable is active. Most variables only perform synchronous activation today,
     * but if the activate returns a Promise wait for it to be resolved before returning.
     */
    // eslint-disable-next-line no-unused-vars
    activate(currentScope, availableContexts) {
      // only activate if it has not been previously
      if (this.lifecycleStage === Constants.VariableLifecycleStage.INIT) {
        if (this.extendedType || Utils.isExtendedType(this._initialValue)) {
          // explicit check to ensure duck typed extended types don't break!
          let ret;
          if (typeof this._initialValue.activateAsync === 'function') {
            // some extended types implements activateAsync for asynchronous
            // activation.
            ret = this._initialValue.activateAsync();
          } else if (typeof this._initialValue.activate === 'function') {
            // currently activate method on extended types can be async. If it
            // returns a Promise then wait for that to resolve before finishing activation
            ret = Promise.resolve(this._initialValue.activate());
          } else {
            ret = Promise.resolve();
          }
          return ret.then(() => {
            this.lifecycleStage = Constants.VariableLifecycleStage.ACTIVE;
          });
        }
        this.lifecycleStage = Constants.VariableLifecycleStage.ACTIVE;
      }
      return Promise.resolve();
    }

    /**
     * Called during the 'dispose' lifecycle stage of a variable, typically when a variable is being torn down.
     * Particularly of use to extended types that might need to perform cleanup tasks like unwind async tasks or
     * unregister listeners or notify their subscribers.
     */
    dispose() {
      this.log.finer('disposing variable', this.name);
      this.lifecycleStage = Constants.VariableLifecycleStage.DISPOSE;
      // call dispose on builtin type variables
      if (this.extendedType || Utils.isExtendedType(this._initialValue)) {
        // explicit check to ensure duck typed extended types don't break!
        if (typeof this._initialValue.dispose === 'function') {
          this._initialValue.dispose();
        }
      }

      if (this.scope.silent === false) {
        // remove all immediate dispatch listeners
        if (this.onValueChangedImmediate) {
          this.onValueChangedImmediate.removeAll();
        }
        this.onValueChanged.removeAll(); // removes all listeners from Signal

        if (this.eventThrottleState) {
          clearTimeout(this.eventThrottleState.timer); // clean up outstanding timer!
        }
        this.eventThrottleState = null;
        this.eventMicroTaskState = null;
      }

      // Dispose of observable to remove dependencies
      if (this.computedValue) {
        this.computedValue.dispose();
        this.computedValue = undefined;

        // We need to dispose expressions in the default value. For example, the following default value
        // will reevaluate and fail if flowVar is updated after pageVar is disposed:
        //
        // "pageVar": {
        //   "defaultValue": {
        //     "prop1": "{{ $flow.variables.flowVar.prop1 }}".
        //     "prop2": "{{ $page.variables.pageVar.prop1 }}
        //   }
        // }
        //
        // Note that the self-referencing expression for prop2 will keep the dependency alive if not disposed.
        Variable.disposeExpressions(this.defaultValue);
        this.defaultValue = undefined;
      }

      this.oldComputedValue = undefined;
    }

    /**
     * Recursively dispose all expressions (computed observables) contained in the given value.
     *
     * @param value value containing expressions
     */
    static disposeExpressions(value) {
      if (!value) {
        return;
      }

      if (ko.isComputed(value)) {
        value.dispose();
      } else if (Utils.isCloneable(value)) {
        // only need to recurse if value is a plain object or array
        Object.values(value).forEach((v) => {
          this.disposeExpressions(v);
        });
      }
    }

    /**
     * Clones the variable's data. This will result in a disconnected structure that can be modified
     * or used independently of this variables data.
     *
     * If the variable was a complex object containing expressions, those expressions would not be
     * evaluated, but rather preserved in the cloned structure.
     *
     * @returns {*} The cloned data structure or primitive
     */
    cloneData() {
      return Variable.unwrapAndClone(this.getValue());
    }

    static unwrapAndClone(value) {
      return Variable.cloneWithMarkers(value, [SKIP_EVAL_EXPRESSIONS_MARKER, RETR_NEW_VALUE_MARKER]);
    }

    /**
     * Called to clone the variable value with a special marker property defined on the object
     * before cloning. This marker is used for optimizing accessors for property (see wrapObject).
     * so that when the clone recurses through the property accessors, the presence of this
     * marker will return the last evaluated value (or whatever the presence of the marker
     * indicates) rather than re-evaluate every time.
     * marker will return the last evaluated value, rather than re-evaluate every time. Once the
     * cloning is done we delete the marker.
     * Note 1: deleting the marker invalidates the Inline Caches, that JS engine sets up for
     * optimization, negatively affecting performance
     * Note 2: When defineProperty is called on the instance aka the literal value, the call reverts
     * the property getters and setters back for other properties.
     *
     * @param {any} value
     * @param {Array<string>} [markers]
     * @returns {*}
     */
    static cloneWithMarkers(value, markers = []) {
      // for builtin types no cloning with markers is necessary
      if (Utils.isCloneable(value)) {
        const obj = value;

        // When defineProperty is called on the instance aka the literal value, the call reverts
        // the property getters and setters back for other properties.
        // Another problem is we define the marker property on obj and then immediately delete the
        // marker both from the original obj (in finally) and on the cloned value (in the try).
        // The cloned object doesn't even have it to begin with.
        markers.forEach((marker) => {
          Object.defineProperty(obj, marker, {
            enumerable: false,
            configurable: true,
            value: true,
          });
        });

        try {
          const newObj = Array.isArray(obj) ? [] : {};
          Utils.cloneObject(obj, newObj);

          markers.forEach((marker) => {
            delete newObj[marker];
          });

          return newObj;
        } finally {
          markers.forEach((marker) => {
            delete obj[marker];
          });
        }
      }
      return value;
    }
  }

  return Variable;
});

